// The organization filter on the Customers tab.
//
// A TYPEAHEAD, NOT A <select>, and that is forced rather than chosen:
// `GET /api/organizations` (docs/API.md §4.1) declares `q` as
// `{ required: true, minLength: 2 }`, so there is no list-all mode anywhere in the
// API and an option list has nothing to be populated from. Building one would mean
// a new endpoint; asking the agent to type two characters does not.
//
// Built on the kit the create form already uses — `useTypeahead` + `ResultList` +
// `useListNavigation` — rather than a fourth hand-rolled combobox. Those three
// modules already solve the parts that are easy to get wrong and invisible when you
// do: previous rows stay on screen while the next request runs, a superseded
// request is aborted so a slow answer for "Blue" cannot overwrite "Bluebird", the
// options are never focusable, and selection is bound to mousedown rather than
// click because click fires after blur.
//
// The selected organization is shown as a removable chip rather than as text left
// in the input. A filter that is applied but no longer visible in the control that
// applied it is how an agent concludes the directory is empty.

import { useCallback, useState } from 'react';

import { api } from '../../api/client.js';
import useTypeahead from '../../components/create/useTypeahead.js';
import useListNavigation from '../../components/create/useListNavigation.js';
import ResultList from '../../components/create/ResultList.jsx';

/**
 * @param {object} props
 * @param {string} props.organizationId   '' when nothing is chosen.
 * @param {string} props.organizationName Display only — it rides in the URL beside
 *   the id so a shared link can label this control without a round trip.
 * @param {(next: {organizationId: string, organizationName: string}) => void} props.onChange
 */
export default function OrganizationPicker({ organizationId, organizationName, onChange }) {
  const [query, setQuery] = useState('');

  // MUST be useCallback-stable. `useTypeahead` lists `fetcher` in its effect's
  // dependency array, so a fetcher rebuilt every render restarts the search on
  // every render — which looks like a slow, flickering list rather than like a bug.
  const searchOrganizations = useCallback(
    (q, signal) =>
      api
        .get(`/organizations?q=${encodeURIComponent(q)}&limit=10`, { signal })
        .then((response) => response.data ?? []),
    [],
  );

  const typeahead = useTypeahead({
    query,
    fetcher: searchOrganizations,
    minLength: 2,
    // Nothing to search for while a scope is already applied; the chip's Clear
    // button is the way out.
    enabled: !organizationId,
  });

  const select = useCallback(
    (row) => {
      setQuery('');
      onChange({ organizationId: String(row.id), organizationName: row.name ?? '' });
    },
    [onChange],
  );

  const listNav = useListNavigation(typeahead.items, {
    onSelect: select,
    onEscape: () => setQuery(''),
  });

  if (organizationId) {
    return (
      <div className="w-64">
        <span className="field-label">Organization</span>
        <div className="flex h-10 items-center gap-2 rounded-md border border-line bg-sunken px-3">
          <span className="min-w-0 flex-1 truncate text-sm text-ink" title={organizationName || undefined}>
            {/* A shared link can carry an id with no name — say which, rather than
                rendering an empty chip that looks like nothing is applied. */}
            {organizationName || `Organization #${organizationId}`}
          </span>
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-brand-700 hover:underline dark:text-brand-300"
            onClick={() => onChange({ organizationId: '', organizationName: '' })}
          >
            Clear
          </button>
        </div>
        {/* The same reserved hint line the search box keeps, so swapping between
            the chip and the input does not move the row below it. */}
        <p className="mt-1 h-4 text-xs text-ink-muted" />
      </div>
    );
  }

  const showList = typeahead.isActive && typeahead.items.length > 0;

  return (
    <div className="relative w-64">
      <label className="field-label" htmlFor="customer-organization">
        Organization
      </label>
      <input
        id="customer-organization"
        type="search"
        className="field-input"
        placeholder="Name or account number"
        autoComplete="off"
        // The combobox pattern: the input keeps focus and points at the highlighted
        // row. Nothing on this screen may trap focus.
        role="combobox"
        aria-expanded={showList}
        aria-controls="customer-organization-listbox"
        aria-autocomplete="list"
        aria-activedescendant={
          showList ? `customer-organization-option-${listNav.activeIndex}` : undefined
        }
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={listNav.handleKeyDown}
      />
      <p className="mt-1 h-4 text-xs text-ink-muted">
        {typeahead.status === 'error'
          ? 'Organization lookup is unavailable.'
          : typeahead.isActive && typeahead.status === 'ready' && typeahead.items.length === 0
            ? 'No organizations match.'
            : ''}
      </p>

      {showList && (
        // Absolutely positioned so opening the list does not shove the table down
        // the page — the filter card is above the results an agent is reading.
        <div className="absolute left-0 right-0 top-full z-10">
          <ResultList
            idPrefix="customer-organization"
            label="Organizations"
            items={typeahead.items}
            activeIndex={listNav.activeIndex}
            onHover={listNav.setActiveIndex}
            onSelect={select}
            getKey={(item) => item.id}
            renderItem={(item) => (
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate font-medium">{item.name}</span>
                {/* text-ink-muted is 1.2:1 on the highlighted row's brand-600 fill;
                    brand-50 on brand-600 is 4.7:1. Same exception ResultList's own
                    header documents. */}
                <span className="shrink-0 text-xs text-ink-muted group-data-[active=true]:text-brand-50">
                  {item.matched_location
                    ? item.matched_location.account_number
                    : `${item.location_count ?? 0} store${item.location_count === 1 ? '' : 's'}`}
                </span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
